/**
 * The strip above the transcript: which pebble, which plane, what is live right
 * now, and what the payload could not tell us.
 *
 * The two flags at the top are NOT interchangeable, and PD-1 spent a review
 * round making them distinguishable, so this component keeps them apart:
 *
 *   `exists: false`  no such pebble — an operator typo, or a pebble whose
 *                    conversation has not started. Not a failure.
 *   `degraded: true` a STORE is broken or unconfigured. The screen is showing
 *                    less than the truth and cannot say how much less.
 *
 * `plane.rtdb` (`/meta.plane`, authoritative for which writer serves a turn) and
 * `plane.postgres` (`pebbles.chat_plane`, the mirror the triggers read) are both
 * shown, and a disagreement between them is called out: that is a live PA-16
 * hazard — the plane the triggers see diverging from the plane the writers use —
 * and this screen is where it is supposed to become visible.
 */
import type { ConversationTrace } from '../../api/types';
import { Badge, Empty, Field, RawJson } from './ui';
import { formatClock, num, obj, sourceIssues, str } from './trace';

function planeLabel(v: unknown): string {
  return str(v) ?? '—';
}

export default function TraceHeader({ trace }: { trace: ConversationTrace }) {
  const issues = sourceIssues(trace);
  const plane = trace.plane;
  const disagree =
    str(plane?.rtdb) !== null && str(plane?.postgres) !== null && str(plane?.rtdb) !== str(plane?.postgres);
  const rtdb = trace.rtdb;
  const state = obj(rtdb?.state);
  const statusNode = obj(rtdb?.status);
  const conversation = obj(statusNode?.conversation);
  const match = obj(statusNode?.match);
  const claim = obj(rtdb?.reconcile);
  const systemTurn = obj(rtdb?.system_turn_request);
  const d1 = trace.d1;

  return (
    <div className="card space-y-4" data-testid="trace-header">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Conversation trace</h2>
          <code className="text-xs text-gray-600 break-all">{trace.pebble_id}</code>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!trace.exists && (
            <Badge tone="gray" title="No store holds anything for this id. Not an error — check the id.">
              no such pebble
            </Badge>
          )}
          {trace.degraded && (
            <Badge tone="red" title="A store is broken or unconfigured — this trace is incomplete by an unknown amount.">
              degraded
            </Badge>
          )}
          {trace.timeline_meta?.head_truncated && <Badge tone="amber">transcript truncated</Badge>}
          {(trace.timeline_meta?.possible_duplicates ?? 0) > 0 && (
            <Badge tone="amber">{trace.timeline_meta.possible_duplicates} possible duplicates</Badge>
          )}
          {num(trace.timings_ms?.total_ms) !== null && (
            <Badge tone="gray" title="Server-side wall clock for the whole join.">
              {trace.timings_ms.total_ms} ms
            </Badge>
          )}
        </div>
      </div>

      {issues.length > 0 && (
        <div className="space-y-1" data-testid="source-issues">
          {issues.map((issue) => (
            <div
              key={issue.name}
              className={`text-xs rounded border px-2 py-1 ${
                issue.severity === 'error'
                  ? 'text-red-800 bg-red-50 border-red-200'
                  : 'text-amber-800 bg-amber-50 border-amber-200'
              }`}
            >
              <strong>{issue.name}</strong>: {issue.status.status}
              {issue.status.truncated ? ` · truncated (dropped ${issue.status.dropped ?? 'rows'})` : ''}
              {issue.status.error ? ` · ${issue.status.error}` : ''}
              {issue.status.detail ? ` — ${issue.status.detail}` : ''}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Plane">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={disagree ? 'red' : 'purple'}>rtdb: {planeLabel(plane?.rtdb)}</Badge>
            <Badge tone={disagree ? 'red' : 'gray'}>pg: {planeLabel(plane?.postgres)}</Badge>
            {plane?.flipped && <Badge tone="amber">flipped @ #{plane.first_split_seq}</Badge>}
          </div>
          <div className="mt-1 text-xs text-gray-600">
            {plane?.split ?? 0} split · {plane?.legacy ?? 0} legacy turns
          </div>
          {disagree && (
            <div className="mt-1 text-xs text-red-700">
              `/meta.plane` and `pebbles.chat_plane` disagree — the triggers and the writers are on different planes.
            </div>
          )}
        </Field>

        <Field label="Live conversation status">
          {conversation ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={str(conversation.kind) === 'processing' ? 'blue' : 'gray'}>
                {str(conversation.kind) ?? 'unknown'}
              </Badge>
              <span className="text-xs text-gray-600">{formatClock(num(conversation.at))}</span>
              <code className="text-xs text-gray-500 break-all">{str(conversation.based_on) ?? 'no based_on'}</code>
            </div>
          ) : (
            <Empty>no /status/conversation</Empty>
          )}
          {claim && (
            <div className="mt-1 text-xs text-amber-800">
              reconcile claim held · attempt {num(claim.attempt) ?? '?'} · based on{' '}
              <code>{str(claim.based_on) ?? '—'}</code>
            </div>
          )}
          {systemTurn && (
            <div className="mt-1 text-xs text-blue-800">
              system turn requested: {str(systemTurn.reason) ?? '(no reason)'}
            </div>
          )}
        </Field>

        <Field label="Live match status">
          {match ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone="blue">{str(match.kind) ?? 'unknown'}</Badge>
              {str(match.client_event) && <Badge tone="gray">{str(match.client_event)}</Badge>}
              {match.must_tell_user === true && <Badge tone="amber">must tell user</Badge>}
              <span className="text-xs text-gray-600">{formatClock(num(match.at))}</span>
            </div>
          ) : (
            <Empty>no /status/match</Empty>
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Reasoner state">
          {state ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone="gray">{str(state.story_type) ?? 'unknown'}</Badge>
              <Badge tone="blue">{str(state.phase) ?? 'unknown phase'}</Badge>
              <span className="text-xs text-gray-600">
                v{num(state.story_version) ?? 0} · {num(state.submitted_count) ?? 0} submits
              </span>
              {(num(state.reconcile_failures) ?? 0) > 0 && (
                <Badge tone="red">{num(state.reconcile_failures)} consecutive failures</Badge>
              )}
            </div>
          ) : (
            <Empty>no /state</Empty>
          )}
        </Field>

        <Field label="matcher D1">
          <div className="text-xs text-gray-700">
            {d1?.stories?.length ?? 0} stories · {d1?.matches?.length ?? 0} matches ·{' '}
            {d1?.refinement_sessions?.length ?? 0} refinement sessions · {d1?.notify_log?.length ?? 0} doorbells ·{' '}
            {d1?.match_event_requests?.length ?? 0} ingest rows
          </div>
        </Field>

        <Field label="Source timings">
          <div className="text-xs text-gray-600 space-x-2">
            {Object.entries(trace.timings_ms ?? {})
              .filter(([k]) => k !== 'total_ms')
              .map(([k, v]) => (
                <span key={k}>
                  {k} {v}ms
                </span>
              ))}
          </div>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <RawJson value={rtdb} label="live RTDB nodes (meta, state, directive, reconcile, status, system_turn_request)" />
        <RawJson value={d1} label="matcher D1 rows (as stored)" />
      </div>
    </div>
  );
}
